import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import DriverSelector from '../../components/driverSelector';
import { Form, Alert, Spinner, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import axios from 'axios';

const GenerateInvoice = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthsData, setMonthsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [error, setError] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [existingInvoices, setExistingInvoices] = useState(null);
  const [editableInvoices, setEditableInvoices] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState(0);

  const months = [
    { value: '01', label: t('january') },
    { value: '02', label: t('february') },
    { value: '03', label: t('march') },
    { value: '04', label: t('april') },
    { value: '05', label: t('may') },
    { value: '06', label: t('june') },
    { value: '07', label: t('july') },
    { value: '08', label: t('august') },
    { value: '09', label: t('september') },
    { value: '10', label: t('october') },
    { value: '11', label: t('november') },
    { value: '12', label: t('december') }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Завантаження списку водіїв
  const fetchDrivers = async () => {
    try {
      const response = await axios.get("/api/users", { 
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } 
      });
      const formattedDrivers = response.data.map((driver) => ({
        value: driver.id,
        label: `${driver.firstName} ${driver.lastName}`
      }));
      return formattedDrivers;
    } catch (err) {
      console.error("Помилка завантаження списку водіїв:", err);
      return [];
    }
  };

  // Завантаження даних тижнів з сервера
  const fetchMonthsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get('/api/monthly-reports/months', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMonthsData(response.data.data);
    } catch (err) {
      console.error('Помилка завантаження даних тижнів:', err);
      setError('Помилка завантаження даних тижнів');
    } finally {
      setLoading(false);
    }
  };

  // Завантаження даних при монтуванні компонента
  useEffect(() => {
    const initializeData = async () => {
      // Завантажуємо дані тижнів
    fetchMonthsData();
      
      // Завантажуємо список водіїв
      const loadedDrivers = await fetchDrivers();
    
    // Отримуємо параметри з URL
    const driverId = searchParams.get('driverId');
    const month = searchParams.get('month');
    const year = searchParams.get('year');
    
      if (driverId && month && year && loadedDrivers.length > 0) {
        // Знаходимо водія зі списку
        const driver = loadedDrivers.find(d => d.value === parseInt(driverId));
        
        if (driver) {
          setSelectedDriver(driver);
      setSelectedMonth(month);
      setSelectedYear(parseInt(year));
      
          // Автоматично завантажуємо дані для попереднього перегляду
          await fetchDriverReports(driver, month, parseInt(year));
        }
      }
    };
    
    initializeData();
    // Ініціалізація має запускатися лише при зміні URL-параметрів; fetchDriverReports читає вже передані значення.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Завантаження існуючих рахунків
  const fetchExistingInvoices = async (driver = null, month = null, year = null) => {
    const driverToUse = driver || selectedDriver;
    const monthToUse = month || selectedMonth;
    const yearToUse = year || selectedYear;

    if (!driverToUse || !monthToUse || !yearToUse) {
      console.log('❌ [FETCH EXISTING INVOICES] Відсутні параметри для запиту');
      return;
    }

    try {
      const url = `/api/invoices/check/${driverToUse.value}/${monthToUse}/${yearToUse}`;
      const response = await axios.get(url);
      
      setExistingInvoices(response.data);
    } catch (error) {
      console.error('❌ [FETCH EXISTING INVOICES] Помилка завантаження існуючих рахунків:', error);
      setExistingInvoices(null);
    }
  };

  // Завантаження даних водія з таблиці invoices
  const fetchDriverReports = async (driver = null, month = null, year = null) => {
    // Використовуємо передані параметри або беремо зі стейту
    const driverToUse = driver || selectedDriver;
    const monthToUse = month || selectedMonth;
    const yearToUse = year || selectedYear;

    if (!driverToUse || !monthToUse || !yearToUse) {
      setValidationError('Будь ласка, виберіть водія, місяць та рік');
      return;
    }

    setLoadingReports(true);
    setError(null);
    setReportsData(null);
    setValidationError(null);

    try {
    const driverIdParamRaw = (driverToUse && typeof driverToUse === 'object')
        ? driverToUse.value
        : driverToUse;
      const driverIdParam = parseInt(driverIdParamRaw, 10);
      if (!Number.isFinite(driverIdParam)) {
        setValidationError('Некоректний водій: не вдалося визначити driverId');
        setLoadingReports(false);
        return;
      }
      
      const response = await axios.get(`/api/invoices/driver/${driverIdParam}/data`, {
        params: {
          month: monthToUse,
          year: yearToUse
        }
      });
      
      const data = response.data;
      // Зберігаємо як reportsData агреговані дані по invoices для поточного вигляду
      setReportsData({
        success: true,
        reportCount: data.count,
        totals: { totalPayout: data.totalAmount },
      });
      setExistingInvoices({ count: data.count, invoices: data.invoices });
      
      // Ініціалізуємо редагувані дані
      setTimeout(() => {
        if (data.invoices) {
          const editable = data.invoices.map(invoice => {
            let weeks = invoice.weeks;
            if (typeof weeks === 'string') {
              try {
                weeks = JSON.parse(weeks);
              } catch {
                weeks = [];
              }
            }
            
            // Отримуємо опис з invoice_data або використовуємо дефолтний
            let invoiceData = invoice.invoice_data;
            if (typeof invoiceData === 'string') {
              try {
                invoiceData = JSON.parse(invoiceData);
              } catch {
                invoiceData = {};
              }
            }
            
            return {
              ...invoice,
              weeks: Array.isArray(weeks) ? weeks.map(w => ({ ...w })) : [],
              description: invoiceData?.description || 'Wynajem samochodu'
            };
          });
          setEditableInvoices(editable);
        }
      }, 100);

      // HTML попереднього перегляду повністю збирається на сервері (один набір позицій + суми в таблиці та блоці оплати).
      const finalHtml = data.previewHtml || '';
      console.log(`📄 [PREVIEW HTML] Length: ${finalHtml?.length || 0}`);
      setInvoicePreview({
        invoice_number: data?.invoices?.[0]?.invoice_number || '',
        pdf_buffer: null,
        html: finalHtml,
      });
      
    } catch (err) {
      console.error('❌ [CLIENT] Помилка завантаження даних:', err);
      setError('Помилка завантаження даних водія');
    } finally {
      setLoadingReports(false);
    }
  };

  // Збереження рахунку та завантаження PDF
  const saveAndDownloadInvoice = async () => {
    if (!invoicePreview?.html || !selectedDriver || !selectedMonth || !selectedYear) return;
    
    try {
      setGeneratingInvoice(true);
      const invoiceNumber = invoicePreview?.invoice_number || `${selectedMonth}-${selectedYear.toString().slice(-2)}`;
      const driverName = selectedDriver?.label || 'Driver';
      const fileName = `${invoiceNumber}/${selectedMonth} - ${driverName}.pdf`;
      
      // Спочатку зберігаємо рахунок зі статусом "saved"
      const saveResponse = await axios.post('/api/invoices/save', {
        driverId: selectedDriver.value,
        month: selectedMonth,
        year: selectedYear,
        status: 'saved'
      });

      if (saveResponse.data.success) {
        // Потім генеруємо та завантажуємо PDF
        const pdfResponse = await axios.post('/api/invoices/generate-from-html', {
          html: invoicePreview.html,
          fileName: fileName
        }, { responseType: 'arraybuffer' });

        const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        // Оновлюємо дані про існуючі рахунки
        await fetchExistingInvoices();
      }
    } catch (e) {
      console.error('Помилка збереження та генерації PDF:', e);
      setError('Помилка збереження рахунку або генерації PDF');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Оновлення значення тижня в редагуваних даних
  const updateWeekAmount = (invoiceIndex, weekIndex, newAmount) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex] && updated[invoiceIndex].weeks[weekIndex]) {
      updated[invoiceIndex].weeks[weekIndex].amount = parseFloat(newAmount) || 0;
      setEditableInvoices(updated);
    }
  };

  // Оновлення опису рахунку
  const updateInvoiceDescription = (invoiceIndex, description) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex]) {
      updated[invoiceIndex].description = description;
      setEditableInvoices(updated);
    }
  };

  // Оновлення номера рахунку
  const updateInvoiceNumber = (invoiceIndex, invoiceNumber) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex]) {
      updated[invoiceIndex].invoice_number = invoiceNumber;
      setEditableInvoices(updated);
    }
  };

  // Завантаження останнього номера рахунку
  const fetchLastInvoiceNumber = async () => {
    if (!selectedMonth || !selectedYear) return;
    
    try {
      const response = await axios.get(`/api/invoices/last-number/${selectedMonth}/${selectedYear}`);
      setLastInvoiceNumber(response.data.lastInvoiceNumber || 0);
    } catch (error) {
      console.error('Помилка отримання останнього номера рахунку:', error);
      setLastInvoiceNumber(0);
    }
  };

  // Оновлення тижня в рядку
  const updateWeekField = (invoiceIndex, weekIndex, field, value) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex] && updated[invoiceIndex].weeks[weekIndex]) {
      updated[invoiceIndex].weeks[weekIndex][field] = value;
      setEditableInvoices(updated);
    }
  };

  // Додавання нового рядка до рахунку
  const addWeekRow = (invoiceIndex) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex]) {
      if (!updated[invoiceIndex].weeks) {
        updated[invoiceIndex].weeks = [];
      }
      updated[invoiceIndex].weeks.push({
        week: '',
        amount: 0,
        type: updated[invoiceIndex].payment_method === 'transfer' ? 'payout' : 'cash'
      });
      setEditableInvoices(updated);
    }
  };

  // Видалення рядка з рахунку
  const removeWeekRow = (invoiceIndex, weekIndex) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex] && updated[invoiceIndex].weeks) {
      updated[invoiceIndex].weeks = updated[invoiceIndex].weeks.filter((_, idx) => idx !== weekIndex);
      setEditableInvoices(updated);
    }
  };

  const hasEditableCashInvoice = () => {
    return Array.isArray(editableInvoices) && editableInvoices.some(invoice => invoice.payment_method === 'cash');
  };

  const hasEditableTransferInvoice = () => {
    return Array.isArray(editableInvoices) && editableInvoices.some(invoice => invoice.payment_method === 'transfer');
  };

  const buildManualInvoice = (paymentMethod) => ({
    invoice_number: '',
    driver_id: selectedDriver.value,
    month: selectedMonth,
    year: selectedYear,
    weeks: [{
      week: '',
      amount: 0,
      type: paymentMethod === 'transfer' ? 'payout' : 'cash'
    }],
    seller_data: null,
    buyer_data: null,
    invoice_data: { total: 0, description: 'Wynajem samochodu' },
    payment_method: paymentMethod,
    payment_due_date: new Date().toISOString().split('T')[0],
    bank_account: '',
    status: 'draft',
    description: 'Wynajem samochodu',
    isManualInvoice: true
  });

  // Створення ручного cash-рахунку, коли автоматично не було готівки в рапортах
  const addManualCashInvoice = () => {
    if (!selectedDriver || !selectedMonth || !selectedYear) {
      setValidationError('Будь ласка, виберіть водія, місяць та рік');
      return;
    }

    if (hasEditableCashInvoice()) {
      alert('Рахунок на готівку вже є у списку.');
      return;
    }

    setEditableInvoices((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      buildManualInvoice('cash')
    ]);
  };

  // Створення ручного transfer-рахунку, коли автоматично були тільки готівкові дані
  const addManualTransferInvoice = () => {
    if (!selectedDriver || !selectedMonth || !selectedYear) {
      setValidationError('Будь ласка, виберіть водія, місяць та рік');
      return;
    }

    if (hasEditableTransferInvoice()) {
      alert('Рахунок на переказ вже є у списку.');
      return;
    }

    setEditableInvoices((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      buildManualInvoice('transfer')
    ]);
  };

  // Збереження змін у рахунках
  const saveInvoiceEdits = async () => {
    if (!editableInvoices || !selectedDriver) return;
    
    setSavingEdits(true);
    try {
      // Отримуємо дані водія
      const driverResponse = await axios.get(`/api/users/${selectedDriver.value}`);
      const driverData = driverResponse.data;
      
      // Оновлюємо кожен рахунок
      for (const invoice of editableInvoices) {
        const weeks = Array.isArray(invoice.weeks) ? invoice.weeks : [];
        const isCashInvoice = invoice.payment_method === 'cash';
        const invoiceData = {
          invoice_number: invoice.invoice_number,
          driver_id: selectedDriver.value,
          month: selectedMonth,
          year: selectedYear,
          weeks: JSON.stringify(weeks),
          seller_data: invoice.seller_data || JSON.stringify({
            name: `${driverData.firstName} ${driverData.lastName}`,
            address: `${driverData.street || ''} ${driverData.house_number || ''}`.trim(),
            city: `${driverData.index || ''}, ${driverData.city || ''}`.replace(/^,\s*/, ''),
            pesel: driverData.pesel || ''
          }),
          buyer_data: invoice.buyer_data || JSON.stringify({
            name: "TORI Viktoriya Manachynska",
            address: "ul. Księcia Witolda 49/15",
            city: "50-202 Wrocław",
            nip: "7822599564"
          }),
          invoice_data: JSON.stringify({
            total: weeks.reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0),
            description: invoice.description || 'Wynajem samochodu'
          }),
          payment_method: invoice.payment_method,
          payment_due_date: invoice.payment_due_date || new Date().toISOString().split('T')[0],
          bank_account: isCashInvoice ? '' : (driverData.account_number || ''),
          status: invoice.status || 'draft'
        };
        
        await axios.put('/api/invoices/upsert', invoiceData);
      }
      
      alert('✅ Зміни успішно збережено!');
      setShowEditModal(false);
      
      // Перезавантажуємо дані
      await fetchDriverReports();
    } catch (error) {
      console.error('Помилка збереження змін:', error);
      alert('❌ Помилка збереження змін: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingEdits(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Форма зверху - приліплена до верху */}
      <div className="bg-light border-bottom p-3 sticky-top">
        <div className="container">
          <h2 className="mb-3">📄 Генерація PDF рахунку</h2>
          <Row>
            <Col md={3}>
              <label className="form-label fw-bold">{t("chooseDriver")}</label>
              <DriverSelector 
                selectedDriver={selectedDriver} 
                setSelectedDriver={setSelectedDriver} 
              />
            </Col>
            <Col md={3}>
              <label className="form-label fw-bold">{t("selectMonth")}</label>
              <Form.Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">{t("selectMonth")}</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <label className="form-label fw-bold">{t("year")}</label>
              <Form.Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <label className="form-label fw-bold">&nbsp;</label>
              <div>
                <Button 
                  variant="primary" 
                  onClick={() => fetchDriverReports()}
                  disabled={loadingReports || loading}
                  className="w-100"
                >
                  {loadingReports ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t("loading")}...
                    </>
                  ) : (
                    `📊 ${t("loaddata")}`
                  )}
                </Button>
              </div>
            </Col>
          </Row>
          
          {/* Кнопка збереження та завантаження PDF */}
          {invoicePreview?.html && (
            <Row className="mt-3">
              <Col md={12}>
                <div className="d-grid">
                  <Button 
                    variant="success" 
                    onClick={saveAndDownloadInvoice}
                    disabled={generatingInvoice}
                    size="lg"
                  >
                    {generatingInvoice ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Збереження та генерація PDF...
                      </>
                    ) : (
                      '💾 Зберегти і завантажити PDF'
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>

      {/* Попередній перегляд рахунку в форматі A4 */}

        {/* Спінер під час завантаження */}
        {loadingReports && (
          <div className="container mt-4">
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 fs-5">⏳ Завантаження даних рапортів...</p>
            </div>
          </div>
        )}

        {/* Попередження про відсутність збережених рапортів */}
        {!loadingReports && reportsData && reportsData.reportCount === 0 && selectedDriver && selectedMonth && selectedYear && (
          <div className="container mt-4">
            <Alert variant="warning">
              <Alert.Heading>⚠️ Немає збережених рапортів</Alert.Heading>
              <p>
                Для водія <strong>{selectedDriver.label}</strong> за <strong>{months.find(m => m.value === selectedMonth)?.label} {selectedYear}</strong> немає збережених та опублікованих місячних рапортів.
              </p>
              <hr />
              <p className="mb-0">
                📋 Перейдіть на сторінку <a href="/admin/monthly-reports" className="alert-link">Місячні рапорти</a> для перевірки, редагування та збереження даних за цей місяць.
              </p>
            </Alert>
          </div>
        )}

        {/* Інформація про існуючі рахунки */}
        {!loadingReports && reportsData && reportsData.reportCount > 0 && existingInvoices && existingInvoices.count > 0 && selectedDriver && (
          <div className="container mt-4">
            <Alert variant="info">
              <Alert.Heading>📄 Існуючі рахунки</Alert.Heading>
              <p>
                Для водія <strong>{selectedDriver.label}</strong> за <strong>{months.find(m => m.value === selectedMonth)?.label} {selectedYear}</strong> вже створено <strong>{existingInvoices.count}</strong> рахунків:
              </p>
              <ul className="mb-2">
                {existingInvoices.invoices.map((invoice, index) => (
                  <li key={index}>
                    <strong>Рахунок {invoice.invoice_number}</strong> - {invoice.status === 'draft' ? 'Чернетка' : 'Опубліковано'} 
                    <small className="text-muted ms-2">(створено: {new Date(invoice.created_at).toLocaleDateString('uk-UA')})</small>
                  </li>
                ))}
              </ul>
              <hr />
              <p className="mb-0">
                Ви можете створити новий рахунок або переглянути існуючі на сторінці <a href="/admin/month-summary" className="alert-link">Місячна статистика</a>.
              </p>
            </Alert>
          </div>
        )}

        {/* Попередній перегляд рахунку */}
        {!loadingReports && reportsData && reportsData.reportCount > 0 && selectedDriver && (
          <div className="container mt-4" style={{ overflow: 'hidden' }}>
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>👁️ Попередній перегляд рахунку</h5>
                  {editableInvoices && editableInvoices.length > 0 && (
                    <Button 
                      variant="outline-primary" 
                      onClick={async () => {
                        await fetchLastInvoiceNumber();
                        setShowEditModal(true);
                      }}
                    >
                      ✏️ Редагувати рахунки
                    </Button>
                  )}
                </div>
              
              {/* HTML попередній перегляд в форматі A4 - 80% ширини сторінки */}
              <div 
                className="border shadow-sm mx-auto" 
                style={{
                  width: '80%',
                  backgroundColor: 'white',
                  overflow: 'visible',
                  position: 'relative'
                }}
              >
                <iframe
                  srcDoc={invoicePreview?.html || ''}
                  style={{
                    width: '100%',
                    minHeight: '297mm',
                    border: 'none',
                    display: 'block',
                    overflow: 'visible'
                  }}
                  scrolling="no"
                  title="Invoice Preview"
                  onLoad={(e) => {
                    // Автоматично підлаштовуємо висоту iframe під контент
                    try {
                      const iframe = e.target;
                      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                      if (iframeDoc.body) {
                        const height = Math.max(
                          iframeDoc.body.scrollHeight,
                          iframeDoc.body.offsetHeight,
                          iframeDoc.documentElement.clientHeight,
                          iframeDoc.documentElement.scrollHeight,
                          iframeDoc.documentElement.offsetHeight
                        );
                        iframe.style.height = height + 'px';
                      }
                    } catch (err) {
                      console.warn('Не вдалося автоматично налаштувати висоту iframe:', err);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Завантаження даних */}
      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" />
          <p className="mt-2">{t("loading")}...</p>
        </div>
      )}

      {/* Помилка валідації */}
      {validationError && (
        <Alert variant="warning" className="mt-4">
          <Alert.Heading>⚠️ Увага!</Alert.Heading>
          {validationError}
        </Alert>
      )}

      {/* Помилка завантаження */}
      {error && (
        <Alert variant="danger" className="mt-4">
          <Alert.Heading>Помилка!</Alert.Heading>
          {error}
          <hr />
          <button className="btn btn-outline-danger" onClick={fetchMonthsData}>
            Спробувати ще раз
          </button>
        </Alert>
      )}


      {/* Повідомлення про вибір місяця */}
      {!loading && !error && monthsData && (!selectedMonth || !selectedYear) && (
        <Alert variant="info" className="mt-4">
          <Alert.Heading>Виберіть місяць та рік</Alert.Heading>
          Для перегляду тижнів виберіть місяць та рік у формах вище.
        </Alert>
      )}

      {/* Модальне вікно для редагування рахунків */}
      <Modal 
        show={showEditModal} 
        onHide={() => setShowEditModal(false)} 
        size="xl"
        scrollable={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>✏️ Редагування рахунків</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Інформація про останній номер рахунку */}
          <Alert variant="info" className="mb-3">
            <strong>Останній номер рахунку за місяць:</strong> {lastInvoiceNumber} 
            <br />
            <small className="text-muted">Наступні вільні номери: {lastInvoiceNumber + 1}, {lastInvoiceNumber + 2}</small>
          </Alert>

          {editableInvoices && !hasEditableCashInvoice() && (
            <Alert variant="warning" className="mb-3">
              <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                <div>
                  <strong>Немає рахунку на готівку.</strong>
                  <div className="small">
                    Якщо готівки не було в автоматичних даних, можна створити cash-рахунок вручну та вписати суму.
                  </div>
                </div>
                <Button variant="outline-warning" onClick={addManualCashInvoice}>
                  ➕ Створити рахунок на готівку
                </Button>
              </div>
            </Alert>
          )}

          {editableInvoices && !hasEditableTransferInvoice() && (
            <Alert variant="warning" className="mb-3">
              <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                <div>
                  <strong>Немає рахунку на переказ.</strong>
                  <div className="small">
                    Якщо автоматично є тільки готівка, можна створити transfer-рахунок вручну та вписати суму.
                  </div>
                </div>
                <Button variant="outline-warning" onClick={addManualTransferInvoice}>
                  ➕ Створити рахунок на переказ
                </Button>
              </div>
            </Alert>
          )}

          {editableInvoices && editableInvoices.length > 0 ? (
            editableInvoices.map((invoice, invoiceIndex) => (
              <div key={invoiceIndex} className="mb-4 border p-3 rounded">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <span className="badge bg-secondary me-2">
                      {invoice.payment_method === 'transfer' ? 'Переказ' : 'Готівка'}
                    </span>
                  </h6>
                </div>
                
                {/* Поле для номера рахунку */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Номер рахунку:</label>
                  <Form.Control
                    type="text"
                    value={invoice.invoice_number || ''}
                    onChange={(e) => updateInvoiceNumber(invoiceIndex, e.target.value)}
                    placeholder="Введіть номер рахунку"
                  />
                </div>
                
                {/* Поле для опису рахунку */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Опис послуги:</label>
                  <Form.Control
                    type="text"
                    value={invoice.description || 'Wynajem samochodu'}
                    onChange={(e) => updateInvoiceDescription(invoiceIndex, e.target.value)}
                    placeholder="Введіть опис послуги"
                  />
                </div>

                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: '30%' }}>Тиждень</th>
                      <th style={{ width: '40%' }}>Сума (zł)</th>
                      <th style={{ width: '20%' }}>Тип</th>
                      <th style={{ width: '10%' }}>Дії</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.weeks && invoice.weeks.length > 0 ? (
                      invoice.weeks.map((week, weekIndex) => (
                        <tr key={weekIndex}>
                          <td>
                            <Form.Control
                              type="text"
                              value={week.week || ''}
                              onChange={(e) => updateWeekField(invoiceIndex, weekIndex, 'week', e.target.value)}
                              placeholder="Номер тижня"
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              step="0.01"
                              value={week.amount || 0}
                              onChange={(e) => updateWeekAmount(invoiceIndex, weekIndex, e.target.value)}
                            />
                          </td>
                          <td>
                            <Form.Select
                              value={week.type || 'payout'}
                              onChange={(e) => updateWeekField(invoiceIndex, weekIndex, 'type', e.target.value)}
                              size="sm"
                            >
                              <option value="payout">Payout</option>
                              <option value="cash">Cash</option>
                            </Form.Select>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeWeekRow(invoiceIndex, weekIndex)}
                            >
                              🗑️
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          Немає даних про тижні
                        </td>
                      </tr>
                    )}
                    <tr className="table-info">
                      <td colSpan="2"><strong>Всього:</strong></td>
                      <td>
                        <strong>
                          {invoice.weeks 
                            ? invoice.weeks.reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0).toFixed(2)
                            : '0.00'} zł
                        </strong>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => addWeekRow(invoiceIndex)}
                        >
                          ➕ Додати
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            ))
          ) : (
            <Alert variant="info">Немає рахунків для редагування</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Скасувати
          </Button>
          <Button 
            variant="primary" 
            onClick={saveInvoiceEdits}
            disabled={savingEdits || !editableInvoices}
          >
            {savingEdits ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Збереження...
              </>
            ) : (
              '💾 Зберегти зміни'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GenerateInvoice;
